import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useTrackFeature, useTrackPromotion} from '@aboutyou/tracking';
import {
  SkeletonBlock,
  Z_INDEXES,
  mediaTabletAndUp,
  useTimedOut,
} from '@aboutyou/ui-components';
import React, {useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {Selection, useRoktLauncher} from './RoktContextProvider';

export interface RoktLancherProps {
  emailsha256: string;
  firstname: string;
  lastname: string;
  confirmationref: string;
  dob: string;
  amount: string;
  currency: string;
  postcode: string;
  country: string;
  language: string;
}

export const RoktLauncher: React.FC<RoktLancherProps> = props => {
  const selectionRef = useRef<Selection>(undefined);
  const [isInteractive, setIsInteractive] = useState(false);
  const {launcher: roktLauncher, isLoading: isLauncherLoading} =
    useRoktLauncher();
  const trackFeature = useTrackFeature();
  const trackPromotion = useTrackPromotion();
  const isTimedOut = useTimedOut(!isLauncherLoading ? 4000 : null);

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    // Return if launcher not initialized or integration attributes not present
    if (!roktLauncher) {
      return;
    }

    const config = {
      attributes: props,
      identifier: 'confirmation.page',
    };

    const selectPlacements = async () => {
      const selection = await roktLauncher.selectPlacements(config);

      selectionRef.current = selection;

      // event documentation https://docs.rokt.com/developers/integration-guides/web/advanced/event-based-integration/#event-types
      selection.on('PLACEMENT_READY').subscribe(value => {
        trackFeature({
          eventType: 'impression',
          label: 'rokt_placement',
        });

        trackPromotion({
          actionType: 'impression',
          promotion: {
            source: TrackingPromotionCreative.BasOspSubscriptionTile,
            promotionId: value.placement?.id ?? '',
          },
          streamPosition: 0,
        });

        selection.on('OFFER_ENGAGEMENT').subscribe(value => {
          trackPromotion({
            actionType: 'click',
            promotion: {
              source: TrackingPromotionCreative.BasOspSubscriptionTile,
              promotionId: value.placement?.id ?? '',
            },
            streamPosition: 0,
          });
        });

        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setIsInteractive(true);
      });
    };

    selectPlacements();

    return () => {
      if (selectionRef.current) {
        // When the page closes, remove all the Rokt placements
        selectionRef.current.close();
      }
    };
  }, [roktLauncher]);

  useEffect(() => {
    if (!isLauncherLoading) {
      if (isTimedOut && !isInteractive) {
        // Force interactivity to remove the spinner. This is just a failsafe
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setIsInteractive(true);
      }
    }
  }, [isLauncherLoading, isTimedOut]);

  return (
    <Wrapper $withMinHeight={!isInteractive}>
      {!isInteractive && <Loader />}
      <div id="rokt-placeholder" />
    </Wrapper>
  );
};

const Loader = styled(SkeletonBlock)`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: ${Z_INDEXES.OSP_ROKT_LOADER};
`;

const Wrapper = styled.div<{$withMinHeight: boolean}>`
  position: relative;
  margin-bottom: 10px;

  ${({$withMinHeight}) =>
    $withMinHeight &&
    css`
      min-height: 246px;

      @media ${mediaTabletAndUp} {
        min-height: 169px;
      }
    `}
`;
